import type VideoJs from "video.js";

type VideoJsFn = typeof VideoJs;

// video.js plus the Cast/AirPlay plugins and their stylesheets are by far
// the heaviest thing this app ships, and none of it is needed to render a
// grid of thumbnails — only to actually play something. Pulling it in
// through a dynamic import keeps it out of the initial bundle entirely, so
// the first paint costs a fraction of what it used to.
//
// The promise is cached, so the chunk is fetched once per page no matter
// how many times a viewer opens and closes the player.
let loading: Promise<VideoJsFn> | null = null;

export function loadPlayer(): Promise<VideoJsFn> {
  if (loading) return loading;

  loading = (async () => {
    const [videojsMod, chromecastMod, airplayMod, videoJsCss, chromecastCss, airplayCss] =
      await Promise.all([
        import("video.js"),
        import("@silvermine/videojs-chromecast"),
        import("@silvermine/videojs-airplay"),
        import("video.js/dist/video-js.css?inline"),
        import("@silvermine/videojs-chromecast/dist/silvermine-videojs-chromecast.css?inline"),
        import("@silvermine/videojs-airplay/dist/silvermine-videojs-airplay.css?inline"),
      ]);

    const videojs = videojsMod.default;

    // Injected at runtime rather than linked: Kajabi's Custom HTML block
    // strips <link> and <style> tags from pasted markup, so a stylesheet
    // that isn't created in JS never arrives.
    const style = document.createElement("style");
    style.textContent = [videoJsCss.default, chromecastCss.default, airplayCss.default].join("\n");
    document.head.appendChild(style);

    chromecastMod.default(videojs, { preloadWebComponents: false });
    airplayMod.default(videojs);

    return videojs;
  })();

  return loading;
}

// The Cast sender SDK is only usable in a real Chrome tab — an app's
// embedded WebView never has chrome.cast injected — so it's fetched only
// where it can actually work, and never blocks first paint.
export function loadCastSdkIfUsable(isChromeLike: boolean, isAndroidWebView: boolean) {
  if (!isChromeLike || isAndroidWebView) return;
  if (document.querySelector('script[data-cast-sdk="1"]')) return;
  const castSdk = document.createElement("script");
  castSdk.src = "https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1";
  castSdk.dataset.castSdk = "1";
  document.head.appendChild(castSdk);
}

// Locks to landscape for fullscreen playback, then releases it the moment
// fullscreen exits. Without the release the phone stays rotated after the
// viewer comes out of the video, which reads as the app having broken
// their screen — they're left rotating it back by hand with no idea what
// happened.
export function lockLandscapeForFullscreen() {
  const orientation = (screen as unknown as {
    orientation?: { lock?: (o: string) => Promise<void>; unlock?: () => void };
  }).orientation;
  if (!orientation?.lock) return;

  orientation.lock("landscape").catch(() => {});

  function release() {
    if (document.fullscreenElement) return; // still fullscreen, leave it
    try {
      orientation?.unlock?.();
    } catch {
      // unsupported — nothing to release
    }
    document.removeEventListener("fullscreenchange", release);
    document.removeEventListener("webkitfullscreenchange", release);
  }

  document.addEventListener("fullscreenchange", release);
  document.addEventListener("webkitfullscreenchange", release);
}
